import React from 'react';
import {
    Box, Container, Typography, Grid, Paper, Stack, Button, Card, CardContent,
    Avatar, Chip, Divider, useTheme, useMediaQuery
} from '@mui/material';
import {
    School, Code, Nature, Public, EmojiEvents, Group,
    LinkedIn, GitHub, Email, Facebook, WhatsApp, Language
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import ImpactStats from '../components/layout/Footer/ImpactStats';
// Import team photos
import alaminPhoto from '../assets/alamin-photo.jpeg';
import saikatPhoto from '../assets/saikat-photo.png';

const AboutPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const teamMembers = [
        {
            name: "Md. Alamin",
            role: "Java Developer & System Design Enthusiast",
            bio: "Passionate Java programmer with Spring Boot, SQL, and system design expertise. Actively exploring Machine Learning (ML) and Artificial Intelligence (AI) to enhance problem-solving capabilities and build intelligent systems. Experienced in competitive programming and technical support, focusing on delivering high-quality, scalable solutions.",
            photo: alaminPhoto,
            portfolio: "https://alamin5g.com",
            skills: ["Java", "Spring Boot", "System Design", "Database Design", "Data Structures", "SQL", "Machine Learning"],
            social: {
                github: "https://github.com/alamin5g",
                linkedin: "https://linkedin.com/in/alamin5g",
                facebook: "https://facebook.com/aalamin5g",
                email: "malaminswe@gmail.com",
                whatsapp: "+8801822679672",
                portfolio: "https://alamin5g.com"
            },
            location: "Tongi, Dhaka, Bangladesh",
            university: "International University of Business Agriculture and Technology",
        },
        {
            name: "Saikat Kumar Gain",
            role: "Java Developer & Researcher",
            bio: "Versatile Java Developer & Researcher passionate about turning complex ideas into seamless, user-friendly solutions. From building scalable Spring Boot backends to crafting engaging React frontends, I merge technical depth with design thinking. As the mind behind Sortify's user experience, I bridge code and creativity — while exploring AI and deep learning to create innovative, impactful applications.",
            photo: saikatPhoto,
            skills: ["Java", "Spring Boot", "SQL", "React", "DSA", "ML"],
            social: {
                github: "https://github.com/SKgain",
                linkedin: "https://www.linkedin.com/in/saikat-kumar-gain-b059422b0",
                facebook: "https://www.facebook.com/share/175jopAU8b/",
                email: "saikatgain.cse@gmail.com",
                whatsapp: "+8801319806363",
                portfolio: "https://saikatgain.online"
            },
            location: "Uttara, Dhaka, Bangladesh",
            university: "International University of Business Agriculture and Technology"
        }
    ];

    const values = [
        {
            icon: <Nature />,
            title: "Environmental Impact",
            description: "Creating technology that makes a real difference for our planet's future"
        },
        {
            icon: <School />,
            title: "Education First",
            description: "Teaching proper waste management through interactive technology"
        },
        {
            icon: <Code />,
            title: "Innovation",
            description: "Leveraging cutting-edge AI to solve traditional problems"
        },
        {
            icon: <Public />,
            title: "Community Driven",
            description: "Building solutions by and for the Bangladeshi community"
        }
    ];

    const achievements = [
        { icon: "🏆", title: "JavaFest 2025", description: "Participating in the prestigious programming competition" },
        { icon: "🤖", title: "AI Innovation", description: "Custom-trained model for Bangladesh waste types" },
        { icon: "🌍", title: "Local Impact", description: "Addressing waste management challenges in Bangladesh" },
        { icon: "👥", title: "Community Focus", description: "Built for local users with local solutions" }
    ];

    return (
        <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f8faf8 0%, #e6f4ea 100%)' }}>
            {/* Hero Section - Mobile Responsive */}
            <Box sx={{
                background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                color: 'white',
                py: { xs: 4, sm: 6, md: 8 }
            }}>
                <Container maxWidth="lg">
                    <Grid container spacing={{ xs: 3, sm: 4 }} alignItems="center">
                        <Grid item xs={12} md={8} order={{ xs: 2, md: 1 }}>
                            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                                <Typography
                                    variant="h2"
                                    fontWeight={800}
                                    gutterBottom
                                    sx={{
                                        fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' },
                                        lineHeight: { xs: 1.2, sm: 1.3 }
                                    }}
                                >
                                    About Sortify 🌱
                                </Typography>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        opacity: 0.9,
                                        mb: { xs: 3, sm: 4 },
                                        fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
                                        lineHeight: { xs: 1.3, sm: 1.4 }
                                    }}
                                >
                                    Empowering Bangladesh with AI-driven waste management solutions
                                </Typography>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={{ xs: 1, sm: 2 }}
                                    flexWrap="wrap"
                                    justifyContent={{ xs: 'center', md: 'flex-start' }}
                                    alignItems={{ xs: 'center', sm: 'flex-start' }}
                                >
                                    <Chip
                                        label="🎓 Student Project"
                                        sx={{
                                            background: 'rgba(255,255,255,0.2)',
                                            color: 'white',
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                        }}
                                    />
                                    <Chip
                                        label="🇧🇩 Made in Bangladesh"
                                        sx={{
                                            background: 'rgba(255,255,255,0.2)',
                                            color: 'white',
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                        }}
                                    />
                                    <Chip
                                        label="🌍 For the Planet"
                                        sx={{
                                            background: 'rgba(255,255,255,0.2)',
                                            color: 'white',
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                        }}
                                    />
                                </Stack>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4} order={{ xs: 1, md: 2 }}>
                            <Box sx={{
                                textAlign: 'center',
                                fontSize: { xs: '4rem', sm: '6rem', md: '8rem' }
                            }}>
                                🎯
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Environmental Impact Stats */}
            <ImpactStats variant="section" />

            {/* Mission Section - Mobile Responsive */}
            <Container maxWidth="lg" sx={{ py: { xs: 6, sm: 8 } }}>
                <Grid container spacing={{ xs: 4, sm: 6 }} alignItems="center">
                    <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
                        <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                            <Typography
                                variant="h3"
                                fontWeight={700}
                                gutterBottom
                                color="#1b5e20"
                                sx={{
                                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                                    lineHeight: { xs: 1.2, sm: 1.3 }
                                }}
                            >
                                🎯 Our Mission
                            </Typography>
                            <Typography
                                variant="body1"
                                paragraph
                                color="#546e7a"
                                sx={{
                                    fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
                                    lineHeight: { xs: 1.6, sm: 1.7 },
                                    textAlign: { xs: 'left', md: 'left' }
                                }}
                            >
                                We believe technology can solve the world's biggest challenges. Starting with waste management
                                in Bangladesh, Sortify uses artificial intelligence to educate citizens about proper waste
                                disposal while making the process fun and rewarding.
                            </Typography>
                            <Typography
                                variant="body1"
                                paragraph
                                color="#546e7a"
                                sx={{
                                    fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
                                    lineHeight: { xs: 1.6, sm: 1.7 },
                                    textAlign: { xs: 'left', md: 'left' }
                                }}
                            >
                                Our goal is to create a cleaner, greener Bangladesh by empowering every citizen with the
                                knowledge and tools they need to manage waste responsibly.
                            </Typography>
                            <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                                <Typography
                                    variant="h6"
                                    fontWeight={600}
                                    color="#43a047"
                                    gutterBottom
                                    sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                                >
                                    🌟 Vision 2030
                                </Typography>
                                <Typography
                                    variant="body1"
                                    color="#546e7a"
                                    sx={{
                                        fontSize: { xs: '0.9rem', sm: '1rem' },
                                        fontStyle: 'italic',
                                        lineHeight: 1.6
                                    }}
                                >
                                    "A Bangladesh where every citizen is an informed eco-warrior, and waste management
                                    is smart, efficient, and sustainable."
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
                        <Box sx={{
                            textAlign: 'center',
                            fontSize: { xs: '6rem', sm: '8rem', md: '12rem' }
                        }}>
                            🌍
                        </Box>
                    </Grid>
                </Grid>
            </Container>

            {/* Values Section - Mobile Responsive */}
            <Box sx={{ background: 'linear-gradient(135deg, #ffffff, #f5fbf7)', py: { xs: 6, sm: 8 } }}>
                <Container maxWidth="lg">
                    <Typography
                        variant="h3"
                        textAlign="center"
                        fontWeight={700}
                        gutterBottom
                        color="#1b5e20"
                        sx={{
                            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                            lineHeight: { xs: 1.2, sm: 1.3 }
                        }}
                    >
                        💝 Our Values
                    </Typography>
                    <Grid container spacing={{ xs: 3, sm: 4 }} sx={{ mt: { xs: 2, sm: 4 } }}>
                        {values.map((value, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <Card sx={{
                                    height: '100%',
                                    textAlign: 'center',
                                    borderRadius: { xs: 3, sm: 4 },
                                    background: 'linear-gradient(135deg, #ffffff, #f8faf8)',
                                    border: '2px solid #43a04715',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: { xs: 'none', sm: 'translateY(-8px)' },
                                        boxShadow: '0 16px 48px rgba(67, 160, 71, 0.15)',
                                        borderColor: '#43a047'
                                    }
                                }}>
                                    <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                                        <Avatar sx={{
                                            width: { xs: 60, sm: 70 },
                                            height: { xs: 60, sm: 70 },
                                            background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                                            mx: 'auto',
                                            mb: { xs: 2, sm: 3 }
                                        }}>
                                            {value.icon}
                                        </Avatar>
                                        <Typography
                                            variant="h6"
                                            fontWeight={700}
                                            gutterBottom
                                            color="#1b5e20"
                                            sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' } }}
                                        >
                                            {value.title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="#546e7a"
                                            sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}
                                        >
                                            {value.description}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Team Section - Mobile Responsive */}
            <Container maxWidth="lg" sx={{ py: { xs: 6, sm: 8 } }}>
                <Typography
                    variant="h3"
                    textAlign="center"
                    fontWeight={700}
                    gutterBottom
                    color="#1b5e20"
                    sx={{
                        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                        lineHeight: { xs: 1.2, sm: 1.3 }
                    }}
                >
                    👥 Meet the Team
                </Typography>
                <Typography
                    variant="h6"
                    textAlign="center"
                    color="#546e7a"
                    paragraph
                    sx={{
                        mb: { xs: 4, sm: 6 },
                        fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                        px: { xs: 2, sm: 0 }
                    }}
                >
                    The passionate developers behind Sortify - The IUBAT Duo
                </Typography>

                <Grid container spacing={{ xs: 3, sm: 4 }} justifyContent="center">
                    {teamMembers.map((member, index) => (
                        <Grid item xs={12} sm={10} md={6} key={index}>
                            <Card sx={{
                                borderRadius: { xs: 3, sm: 4 },
                                background: 'linear-gradient(135deg, #43a04708, #ffffff)',
                                border: '2px solid #43a04715',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: { xs: 'none', sm: 'translateY(-8px)' },
                                    boxShadow: '0 16px 48px rgba(67, 160, 71, 0.15)'
                                }
                            }}>
                                <CardContent sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
                                    {/* Team Member Photo */}
                                    <Avatar
                                        src={member.photo}
                                        alt={member.name}
                                        sx={{
                                            width: { xs: 100, sm: 120 },
                                            height: { xs: 100, sm: 120 },
                                            mx: 'auto',
                                            mb: { xs: 2, sm: 3 },
                                            border: '4px solid #43a047',
                                            boxShadow: '0 12px 40px rgba(67, 160, 71, 0.25)',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: { xs: 'none', sm: 'scale(1.05)' },
                                                boxShadow: '0 16px 48px rgba(67, 160, 71, 0.35)'
                                            }
                                        }}
                                    />

                                    {/* Name with Portfolio Link for Alamin */}
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        alignItems="center"
                                        justifyContent="center"
                                        spacing={{ xs: 1, sm: 2 }}
                                        sx={{ mb: 1 }}
                                    >
                                        <Typography
                                            variant="h5"
                                            fontWeight={700}
                                            color="#1b5e20"
                                            sx={{ fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.5rem' } }}
                                        >
                                            {member.name}
                                        </Typography>
                                        {member.portfolio && (
                                            <Button
                                                href={member.portfolio}
                                                target="_blank"
                                                variant="contained"
                                                size="small"
                                                startIcon={<Language />}
                                                sx={{
                                                    background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                                                    color: 'white',
                                                    fontWeight: 700,
                                                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                    px: { xs: 1.5, sm: 2 },
                                                    py: { xs: 0.5, sm: 0.75 },
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    boxShadow: '0 4px 12px rgba(67, 160, 71, 0.3)',
                                                    '&:hover': {
                                                        background: 'linear-gradient(135deg, #388e3c, #43a047)',
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: '0 6px 16px rgba(67, 160, 71, 0.4)'
                                                    }
                                                }}
                                            >
                                                alamin5g.com
                                            </Button>
                                        )}
                                    </Stack>

                                    <Typography
                                        variant="subtitle1"
                                        color="#43a047"
                                        fontWeight={600}
                                        gutterBottom
                                        sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                                    >
                                        {member.role}
                                    </Typography>

                                    {/* University and Location */}
                                    <Typography
                                        variant="body2"
                                        color="#546e7a"
                                        sx={{
                                            mb: 1,
                                            fontStyle: 'italic',
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                        }}
                                    >
                                        📍 {member.location}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="#546e7a"
                                        sx={{
                                            mb: 2,
                                            fontWeight: 600,
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                        }}
                                    >
                                        🎓 {member.university}
                                    </Typography>

                                    <Typography
                                        variant="body1"
                                        paragraph
                                        color="#546e7a"
                                        sx={{
                                            fontSize: { xs: '0.9rem', sm: '1rem' },
                                            lineHeight: { xs: 1.5, sm: 1.6 }
                                        }}
                                    >
                                        {member.bio}
                                    </Typography>

                                    {/* ...existing skills section... */}

                                    <Divider sx={{ my: 2 }} />
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={1}
                                        justifyContent="center"
                                        flexWrap="wrap"
                                    >
                                        {member.social.portfolio && (
                                            <Button
                                                startIcon={<Language />}
                                                size="small"
                                                sx={{
                                                    color: '#43a047',
                                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                    fontWeight: 600,
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(67, 160, 71, 0.1)',
                                                        transform: 'translateY(-1px)'
                                                    }
                                                }}
                                                href={member.social.portfolio}
                                                target="_blank"
                                            >
                                                Portfolio
                                            </Button>
                                        )}
                                        <Button
                                            startIcon={<GitHub />}
                                            size="small"
                                            sx={{
                                                color: '#43a047',
                                                fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                            }}
                                            href={member.social.github}
                                            target="_blank"
                                        >
                                            GitHub
                                        </Button>
                                        <Button
                                            startIcon={<LinkedIn />}
                                            size="small"
                                            sx={{
                                                color: '#43a047',
                                                fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                            }}
                                            href={member.social.linkedin}
                                            target="_blank"
                                        >
                                            LinkedIn
                                        </Button>
                                        <Button
                                            startIcon={<Facebook />}
                                            size="small"
                                            sx={{
                                                color: '#43a047',
                                                fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                            }}
                                            href={member.social.facebook}
                                            target="_blank"
                                        >
                                            Facebook
                                        </Button>
                                        <Button
                                            startIcon={<Email />}
                                            size="small"
                                            sx={{
                                                color: '#43a047',
                                                fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                            }}
                                            href={`mailto:${member.social.email}`}
                                        >
                                            Email
                                        </Button>
                                        <Button
                                            startIcon={<WhatsApp />}
                                            size="small"
                                            sx={{
                                                color: '#43a047',
                                                fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                            }}
                                            href={`https://wa.me/${member.social.whatsapp.replace(/\D/g, '')}`}
                                            target="_blank"
                                        >
                                            WhatsApp
                                        </Button>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* Achievements Section - Mobile Responsive */}
            <Box sx={{ background: 'linear-gradient(135deg, #ffffff, #f5fbf7)', py: { xs: 6, sm: 8 } }}>
                <Container maxWidth="lg">
                    <Typography
                        variant="h3"
                        textAlign="center"
                        fontWeight={700}
                        gutterBottom
                        color="#1b5e20"
                        sx={{
                            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                            lineHeight: { xs: 1.2, sm: 1.3 }
                        }}
                    >
                        🏆 Our Journey
                    </Typography>
                    <Grid container spacing={{ xs: 3, sm: 4 }} sx={{ mt: { xs: 2, sm: 4 } }}>
                        {achievements.map((achievement, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <Paper sx={{
                                    p: { xs: 2, sm: 3 },
                                    textAlign: 'center',
                                    borderRadius: { xs: 3, sm: 4 },
                                    background: 'linear-gradient(135deg, #ffffff, #f8faf8)',
                                    border: '2px solid #43a04715',
                                    height: '100%',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: { xs: 'none', sm: 'translateY(-4px)' },
                                        boxShadow: '0 12px 32px rgba(67, 160, 71, 0.1)'
                                    }
                                }}>
                                    <Box sx={{
                                        fontSize: { xs: '2.5rem', sm: '3rem' },
                                        mb: { xs: 1.5, sm: 2 }
                                    }}>
                                        {achievement.icon}
                                    </Box>
                                    <Typography
                                        variant="h6"
                                        fontWeight={600}
                                        gutterBottom
                                        color="#1b5e20"
                                        sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' } }}
                                    >
                                        {achievement.title}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="#546e7a"
                                        sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}
                                    >
                                        {achievement.description}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Contact CTA - Mobile Responsive */}
            <Box sx={{
                background: 'linear-gradient(135deg, #1b5e20, #43a047)',
                color: 'white',
                py: { xs: 4, sm: 6 },
                textAlign: 'center'
            }}>
                <Container maxWidth="md">
                    <Typography
                        variant="h3"
                        fontWeight={800}
                        gutterBottom
                        sx={{
                            fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2.5rem' },
                            lineHeight: { xs: 1.2, sm: 1.3 }
                        }}
                    >
                        Let's Build a Greener Future Together! 🤝
                    </Typography>
                    <Typography
                        variant="h6"
                        paragraph
                        sx={{
                            opacity: 0.9,
                            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                            px: { xs: 2, sm: 0 }
                        }}
                    >
                        Have questions, suggestions, or want to collaborate? We'd love to hear from you!
                    </Typography>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        justifyContent="center"
                        sx={{ mt: { xs: 3, sm: 4 } }}
                    >
                        <Button
                            component={Link}
                            to="/contact"
                            variant="contained"
                            size="large"
                            sx={{
                                background: 'linear-gradient(135deg, #ffc400, #ffea00)',
                                color: '#1b5e20',
                                fontWeight: 700,
                                px: { xs: 3, sm: 4 },
                                py: { xs: 1.2, sm: 1.5 },
                                fontSize: { xs: '0.9rem', sm: '1rem' }
                            }}
                        >
                            Get In Touch 📧
                        </Button>
                        <Button
                            component={Link}
                            to="/classify"
                            variant="outlined"
                            size="large"
                            sx={{
                                borderColor: 'white',
                                color: 'white',
                                fontWeight: 700,
                                px: { xs: 3, sm: 4 },
                                py: { xs: 1.2, sm: 1.5 },
                                fontSize: { xs: '0.9rem', sm: '1rem' },
                                '&:hover': {
                                    borderColor: 'white',
                                    backgroundColor: 'rgba(255,255,255,0.1)'
                                }
                            }}
                        >
                            Try Sortify Now 🚀
                        </Button>
                    </Stack>
                </Container>
            </Box>
        </Box>
    );
};

export default AboutPage;
